import { useCallback, useMemo } from 'react'

import { useAccountPreferences } from '~publish/contexts/AccountContext'
import { useUpdateAccountPreferences } from '~publish/pages/Settings/Preferences/hooks/useUpdateAccountPreferences'

/**
 * How unanswered mentions are surfaced in Community. In counter mode they show
 * as a number and contribute to the general Community counter; in dot mode they
 * show as a dot and are excluded from it.
 */
export type MentionsCounterMode = 'counter' | 'dot'

export const DEFAULT_MENTIONS_COUNTER_MODE: MentionsCounterMode = 'counter'

type UseMentionsCounterModeReturn = {
  mode: MentionsCounterMode
  setMode: (mode: MentionsCounterMode) => void
}

/**
 * Read/write the mentions counter mode. The single seam for this preference and
 * the only place that knows how it persists, so consumers never touch the
 * underlying account preference directly.
 *
 * Backed by the account-level `mentionsCounter` preference: read from
 * `account.preferences.community.mentionsCounter` and written via
 * `updateAccountPreferences`. Falls back to the default while the account query
 * is still loading.
 */
export function useMentionsCounterMode(): UseMentionsCounterModeReturn {
  const preferences = useAccountPreferences()
  const [updateAccountPreferences] = useUpdateAccountPreferences()

  const mode =
    preferences?.community?.mentionsCounter ?? DEFAULT_MENTIONS_COUNTER_MODE

  const setMode = useCallback(
    (next: MentionsCounterMode) => {
      void updateAccountPreferences({
        variables: { input: { mentionsCounter: next } },
      })
    },
    [updateAccountPreferences],
  )

  return useMemo(() => ({ mode, setMode }), [mode, setMode])
}
